import { Box } from "@mui/material";
import React, { Dispatch, useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import CustomButton from "../components/common/CustomButton";
import CustomLabel from "../components/common/CustomLabel";
import CustomTextField from "../components/common/CustomTextField";
import {
  Container,
  GlobalReset,
  LeftPanel,
  OuterContainer,
  RightPanel,
} from "../components/layout/CommonLoginLayout";
import { TwoFactorMethod, userPasswordLogin } from "../services/AuthService";
import { COLORS } from "../assets/theme";
import ToastNotification from "../components/ToastNotification";

type PendingOtpSession = {
  uuid: string;
  availableMethods: TwoFactorMethod[];
};

const PENDING_OTP_SESSION_KEY = "pendingOtpSession";

const Login = () => {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("access_token")
  );

  useEffect(() => {
    if (accessToken) localStorage.setItem("access_token", accessToken);
  }, [accessToken]);

  if (accessToken) {
    return <Navigate to="/" />;
  }
  return <UnAuthed setAccessToken={setAccessToken} />;
};

interface IUnAuthedProps {
  setAccessToken: Dispatch<React.SetStateAction<string | null>>;
}

const UnAuthed = ({ setAccessToken }: IUnAuthedProps) => {
  const navigate = useNavigate();
  const [logginIn, setLogginIn] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});

  const validateField = (name: string, value: string) => {
    let errorMessage = "";

    if (name === "email") {
      if (!value) {
        errorMessage = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errorMessage = "Please enter a valid email address.";
      }
    }

    if (name === "password") {
      if (!value) {
        errorMessage = "Password is required.";
      } else if (value.length < 6) {
        errorMessage = "Password must be at least 6 characters.";
      }
    }

    return errorMessage;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    const errorMessage = validateField(name, value);
    setError((prevError) => ({ ...prevError, [name]: errorMessage }));
  };

  const handleLogin = async () => {
    if (logginIn) {
      return;
    }
    setLogginIn(true);
    const { email, password } = formData;
    const newError = {
      email: validateField("email", email),
      password: validateField("password", password),
    };

    if (newError.email || newError.password) {
      setError(newError);
      setLogginIn(false);
      return;
    }

    try {
      const loginResponse = await userPasswordLogin(email, password);

      if (loginResponse && loginResponse.access_token) {
        if (loginResponse.refresh_token) {
          localStorage.setItem("refresh_token", loginResponse.refresh_token);
        }
        setAccessToken(loginResponse.access_token);
        ToastNotification.success("Login successful! Welcome back.");
        window.location.reload();
      } else {
        if (loginResponse?.requiresOtp && loginResponse?.uuid) {
          const availableMethods = Array.isArray(loginResponse.availableMethods)
            ? (loginResponse.availableMethods as TwoFactorMethod[])
            : [];

          const nextSession: PendingOtpSession = {
            uuid: loginResponse.uuid,
            availableMethods,
          };
          sessionStorage.setItem(PENDING_OTP_SESSION_KEY, JSON.stringify(nextSession));

          const msg =
            loginResponse?.message ||
            "Two-factor authentication is required. Please choose a method.";
          ToastNotification.info(msg, { timeOut: 10000 });
          navigate("/choose-2fa");
        } else {
          const msg = loginResponse?.message || "Login failed. Please try again.";
          ToastNotification.error(msg);
          setError({ general: msg });
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || "Login failed. Please try again.";
      setError({ general: errorMessage });
      ToastNotification.error(errorMessage);
    } finally {
      setLogginIn(false);
    }
  };

  return (
    <>
      <Outlet />
      <GlobalReset />
      <OuterContainer>
        <Container>
          <RightPanel>
            <CustomLabel
              text="Login"
              fontWeight="bold"
              fontSize="24px"
              color="#A39455"
              sx={{ marginBottom: 3, textAlign: "left", width: "100%" }}
            />

            {error.general && (
              <CustomLabel
                text={error.general}
                fontSize="14px"
                color="red"
                sx={{ marginBottom: 2, textAlign: "center", width: "100%" }}
              />
            )}

            <CustomTextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              bgColor="#f5f5f5"
              fullWidth
              sx={{ marginBottom: 2 }}
              error={!!error.email}
              helperText={error.email}
              onKeyDown={({ key }) => {
                if (key === "Enter") handleLogin();
              }}
            />

            <CustomTextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              bgColor="#f5f5f5"
              fullWidth
              sx={{ marginBottom: 3 }}
              error={!!error.password}
              helperText={error.password}
              onKeyDown={({ key }) => {
                if (key === "Enter") handleLogin();
              }}
            />

            <CustomButton
              label="Login"
              onClick={handleLogin}
              bgColor="#A39455"
              textColor="#fff"
              borderRadius="4px"
              size="medium"
              sx={{
                marginTop: 8,
                alignSelf: "center",
              }}
              disabled={logginIn}
            />
          </RightPanel>
          <LeftPanel
            sx={{
              maxHeight: { xs: "80vh" },
              pt: { xs: 6, sm: 7, md: 12 },
            }}
          >
            <Box
              component="img"
              src="/SPARK LOGO-white.png"
              alt="Logo"
              sx={{
                width: { xs: "200px", sm: "250px", md: "300px" },
                height: "auto",
                marginBottom: { xs: "10px", md: "20px" },
              }}
            />

            <CustomLabel
              text="Power Your Messages with Spark!"
              sx={{
                fontSize: { xs: "14px", sm: "16px", md: "17px" },
                textAlign: "center",
              }}
              color={COLORS.GOLD}
              marginTop="40px"
              fontWeight="bold"
            />

            <CustomLabel
              text="Unlock the full potential of fast, reliable, and global messaging with our cutting-edge SMS Gateway. Log in now and take your communication to the next level with ease!"
              sx={{
                fontSize: { xs: "12px", sm: "14px", md: "15px" },
                fontWeight: 400,
                lineHeight: "1.5",
                textAlign: "center",
                maxWidth: { xs: "80%", md: "600px" },
                color: "white",
                margin: "0 auto",
              }}
            />

            <CustomLabel
              text="Powered by Teways"
              color="#fff"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "6px", sm: "7px", md: "8px" },
              }}
            />
          </LeftPanel>
        </Container>
      </OuterContainer>
    </>
  );
};

export default Login;
